import { Divider, Heading } from '@byteclaw/visage';
import { GetStaticPaths, GetStaticProps } from 'next';
import {
  ArticleCard,
  ArticleCardData,
  ArticleGrid,
  Layout,
  Paginator,
  SEO,
} from '../../../components';
import {
  createCategoryHref,
  createSocialImageCategoryHref,
} from '../../../helpers/createHref';
import { serializeMdx } from '../../../helpers/serializeMdx';
import { graphqlClient, imageLoader } from '../../../services';
import {
  AllCategoriesDocument,
  AllCategoriesQuery,
  ArticlesCountInCategoryDocument,
  ArticlesCountInCategoryQuery,
  ArticlesCountInCategoryQueryVariables,
  CategoryDocument,
  CategoryQuery,
  CategoryQueryVariables,
} from '../../../queries.graphql';

export interface CategoryPageProps {
  categories: { id: string; name: string; slug: string }[];
  category: {
    id: string;
    name: string;
    slug: string;
    description: string;
  };
  articles: ArticleCardData[];
  pageNumber?: number;
  totalPages: number;
}

export default function CategoryPage({
  categories,
  category,
  articles,
  pageNumber = 1,
  totalPages = 0,
}: CategoryPageProps) {
  return (
    <Layout categories={categories}>
      <SEO
        description={category.description}
        meta={[
          <meta property="og:type" key="og-type" content="website" />,
          <meta
            key="og-published-at"
            property="og:published_time"
            // @ts-expect-error this is not nullable because we require published pages
            content={category.publishedAt}
          />,
          <meta
            key="og-modified-at"
            property="og:modified_time"
            // @ts-expect-error this is not nullable because we require published pages
            content={category.updatedAt}
          />,
        ]}
        path={
          pageNumber > 1
            ? `/${category.slug}/strana/${pageNumber}`
            : `/${category.slug}`
        }
        title={category.name}
        image={createSocialImageCategoryHref('fb', { name: category.name })}
      />
      <Heading styles={{ fontSize: 5, lineHeight: '1.1em' }}>
        {category.name}
      </Heading>
      <Divider
        lineProps={{ styles: { borderBottomWidth: 2 } }}
        styles={{ my: 4 }}
      />
      <ArticleGrid>
        {articles.map((article) => (
          <ArticleCard key={article.id} article={article} />
        ))}
      </ArticleGrid>
      <Paginator
        basePath={createCategoryHref(category)}
        pageNumber={pageNumber}
        totalPages={totalPages}
      />
    </Layout>
  );
}

export const getStaticProps: GetStaticProps<
  CategoryPageProps,
  { categorySlugOrTime: string; pageNumber?: string }
> = async function getStaticProps({ params }) {
  const result = await graphqlClient.request<
    CategoryQuery,
    CategoryQueryVariables
  >(CategoryDocument, {
    slug: params!.categorySlugOrTime!,
    skip: 12 * (Number(params?.pageNumber ?? 1) - 1),
  });

  if (!result.category) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      categories: result.categories,
      category: result.category,
      articles: await Promise.all(
        result.articles.edges.map(async ({ node: { content, ...article } }) => {
          const res = await serializeMdx(content, { imageLoader });

          return {
            ...article,
            timeToRead: res.readTimes,
          };
        }),
      ),
      pageNumber: Number(params?.pageNumber ?? 1),
      totalPages: Math.ceil(result.articles.aggregate.count / 12),
    },
  };
};

export const getStaticPaths: GetStaticPaths<{
  categorySlugOrTime: string;
  pageNumber?: string;
}> = async function getStaticPaths() {
  const result = await graphqlClient.request<AllCategoriesQuery>(
    AllCategoriesDocument,
  );

  const paths: { categorySlugOrTime: string; pageNumber: string }[] = [];

  for (const category of result.articleCategories) {
    if (category.articles.length === 0) {
      // eslint-disable-next-line no-continue
      continue; // skip empty category
    }

    // eslint-disable-next-line no-await-in-loop
    const articlesCountResult = await graphqlClient.request<
      ArticlesCountInCategoryQuery,
      ArticlesCountInCategoryQueryVariables
    >(ArticlesCountInCategoryDocument, {
      id: category.id,
    });

    Array.from({
      length: Math.ceil(
        articlesCountResult.articlesConnection.aggregate.count / 12,
      ),
    }).forEach((_, pageNumber) => {
      // skip first page
      if (pageNumber === 0) {
        return;
      }

      paths.push({
        categorySlugOrTime: category.slug,
        pageNumber: (pageNumber + 1).toString(),
      });
    });
  }

  // now for each category load articlesConnection
  return {
    paths: paths.map((params) => ({ params })),
    fallback: false,
  };
};
